import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download, FileText, BookOpen, Video, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourcesPage() {
  const [guideForm, setGuideForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!guideForm.email || !guideForm.name) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/download-guide`, guideForm, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '1031_Exchange_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Guide downloaded!");
      setGuideForm({ name: "", email: "" });
    } catch (err) {
      toast.error("Failed to download. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resources = [
    {
      icon: FileText,
      title: "Understanding 1031 Exchanges",
      description: "A comprehensive overview of how Section 1031 of the IRS code allows property owners to defer capital gains taxes through like-kind property exchanges.",
      link: "/guide"
    },
    {
      icon: Calculator,
      title: "45/180 Day Timeline",
      description: "Critical deadlines every buyer must know. Learn about the identification period and exchange completion requirements.",
      link: "/process"
    },
    {
      icon: BookOpen,
      title: "Why Condo Units Work",
      description: "Discover why individual condo units have become an increasingly popular choice for 1031 exchange buyers seeking simplified ownership.",
      link: "/guide"
    },
    {
      icon: Video,
      title: "NYC Market Overview",
      description: "An in-depth look at the New York City luxury condo unit market and why it remains attractive for long-term buyers.",
      link: "/nyc"
    },
    {
      icon: Video,
      title: "Miami Market Overview",
      description: "Explore why Miami has emerged as one of the fastest-growing luxury real estate markets in the United States.",
      link: "/miami"
    },
    {
      icon: FileText,
      title: "Common 1031 Mistakes",
      description: "Learn about the most frequent errors buyers make during 1031 exchanges and how to avoid them.",
      link: "/guide"
    },
  ];

  const faqs = [
    {
      question: "What is a 1031 exchange?",
      answer: "A 1031 exchange, named after Section 1031 of the Internal Revenue Code, allows real estate owners to defer capital gains taxes when selling a qualifying property and reinvesting the proceeds into another qualifying 'like-kind' property."
    },
    {
      question: "What is the 45-day identification rule?",
      answer: "After selling your relinquished property, you have exactly 45 calendar days to identify potential replacement properties in writing to your qualified intermediary. This deadline is strictly enforced by the IRS."
    },
    {
      question: "What is the 180-day exchange period?",
      answer: "You must close on your replacement property within 180 days of selling your original property (or by your tax return due date, whichever comes first). This includes weekends and holidays."
    },
    {
      question: "Why are condo units used in 1031 exchanges?",
      answer: "Individual condo units offer simplified ownership, lower operational complexity, and access to properties in established buildings. Different property types may be considered in a 1031 exchange depending on a buyer's objectives, preferences, management expectations, and transaction criteria."
    },
    {
      question: "Do I need a qualified intermediary?",
      answer: "Yes. You must use a qualified intermediary (QI) to hold the proceeds from your property sale. If you take possession of the funds directly, the exchange will be disqualified."
    },
    {
      question: "Can I exchange into multiple properties?",
      answer: "Yes. The 3-Property Rule allows you to identify up to three properties regardless of their value. Alternatively, the 200% Rule allows identification of more properties if their total value doesn't exceed 200% of the sold property."
    },
    {
      question: "Does this website offer securities or investment products?",
      answer: "No. 1031Condo.com is a real estate brokerage website focused on helping clients identify and acquire replacement properties for 1031 exchanges."
    },
    {
      question: "Does Krieger Real Estate LLC provide legal, tax, or investment advice?",
      answer: "No. Krieger Real Estate LLC provides real estate brokerage services only. Clients should consult their own legal, tax, and financial advisors."
    },
    {
      question: "What is your role in the transaction?",
      answer: "Krieger Real Estate LLC acts as a real estate broker and assists clients in identifying, evaluating, and transacting real estate properties."
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline mb-4 block">Buyer Education</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A2E] leading-[1.1] mb-6" data-testid="resources-title">
              Resources & Guides
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Educational resources to help you understand 1031 exchanges and make informed investment decisions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Guide */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="px-3 py-1 bg-[#0066FF] text-white text-xs font-medium uppercase tracking-wider inline-block mb-6">
                Featured Resource
              </span>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight mb-6">
                Free 1031 Exchange Buyer's Guide
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Download our comprehensive guide explaining how buyers use condo units to complete successful 1031 exchanges. Learn about timelines, rules, and common mistakes to avoid.
              </p>
              
              <form onSubmit={handleDownload} className="space-y-4 max-w-md">
                <Input
                  type="text"
                  placeholder="Your Name"
                  value={guideForm.name}
                  onChange={(e) => setGuideForm({ ...guideForm, name: e.target.value })}
                  className="h-12 px-4 rounded-sm border-slate-200"
                  data-testid="resource-name-input"
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={guideForm.email}
                  onChange={(e) => setGuideForm({ ...guideForm, email: e.target.value })}
                  className="h-12 px-4 rounded-sm border-slate-200"
                  data-testid="resource-email-input"
                />
                <Button 
                  type="submit" 
                  className="btn-primary w-full h-12 rounded-sm"
                  disabled={loading}
                  data-testid="resource-download-btn"
                >
                  {loading ? "Generating..." : (
                    <>
                      <Download className="mr-2 w-5 h-5" />
                      Download Free Guide
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            <motion.div
              className="bg-slate-50 p-8 border border-slate-100"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-medium text-[#1A1A2E] mb-6">What's Inside:</h3>
              <ul className="space-y-4">
                {[
                  "Complete overview of 1031 exchanges",
                  "Step-by-step timeline breakdown",
                  "45-day and 180-day deadline strategies",
                  "Why condo units work for exchanges",
                  "NYC and Miami market insights",
                  "Common mistakes to avoid",
                  "Checklist for successful exchanges"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-600">
                    <div className="w-1.5 h-1.5 bg-[#0066FF] rounded-full" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">Learn More</span>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight">
              Educational Content
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource, idx) => (
              <motion.div
                key={resource.title}
                className="group p-8 bg-white border border-slate-100 hover:shadow-float transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <resource.icon className="w-8 h-8 text-[#0066FF] mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-medium text-[#1A1A2E] mb-3">{resource.title}</h3>
                <p className="text-slate-600 mb-6">{resource.description}</p>
                <Link to={resource.link} className="inline-flex items-center text-[#0066FF] font-medium hover:text-[#1D4ED8] transition-colors">
                  Learn More
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <motion.div
                key={faq.question}
                className="p-6 bg-slate-50 border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <h3 className="text-lg font-medium text-[#1A1A2E] mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Have More Questions?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Our team is here to help you understand the 1031 exchange process and identify suitable replacement properties.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#1A1A2E] hover:bg-slate-100 px-8 py-6 text-base rounded-sm">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
